import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ZodError, z } from "zod";
import { apiUrls } from "../helpers/apiUrls";
import { useAxiosPrivate } from "../hooks/useAxiosPrivate";
import { useFormServerSideErrorsHandler } from "../components/form/ErrorsHandler/ErrorsHandler";

export const channelsApi = {
    getChannels: (axios, params) => {
        return axios
            .get(apiUrls.sales.channels.all, {
                params,
            })
            .then((response) => response.data);
    },

    getChannelById: (axios, channelId) => {
        return axios.get(apiUrls.sales.channels.channel(channelId)).then((response) => response.data);
    },

    updateChannel: (axios, channelId, body) => {
        return axios.patch(
            apiUrls.sales.channels.update(channelId),
            body,
        );
    },

    async getFilteringData(axios) {
        const response = await axios.get(apiUrls.sales.channels.filteringData);
        return response.data
    },
};

export function useChannels(params = new URLSearchParams(), options = {}) {
    const axiosPriv = useAxiosPrivate();

    const queryOpts = queryOptions({
        ...options,
        queryKey: ["channels", Object.fromEntries(params.entries())],
        queryFn: () => {
            return channelsApi.getChannels(axiosPriv, params);
        },
        throwOnError: (error) => error instanceof ZodError,
    });

    return useQuery({
        ...queryOpts,
    });
}

export function useChannelsFilteringData(options = {}) {
    const axiosPriv = useAxiosPrivate();

    const queryOpts = queryOptions({
        ...options,
        queryKey: ["channels_filtering_data"],
        queryFn: () => {
            return channelsApi.getFilteringData(axiosPriv);
        },
        throwOnError: (error) => error instanceof ZodError,
    });

    return useQuery({
        ...queryOpts,
    });
}

export function useChannelById(channelId, options = {}) {
    const axiosPriv = useAxiosPrivate();

    const queryOpts = queryOptions({
        ...options,
        queryKey: ["channels", channelId],
        queryFn: () => {
            return channelsApi.getChannelById(axiosPriv, channelId);
        },
    });

    return useQuery({
        ...queryOpts,
    });
}

export function useUpdateChannelMutation(options = {}) {
    const axiosPriv = useAxiosPrivate();
    const queryClient = useQueryClient();
    const { onServerError } = useFormServerSideErrorsHandler();

    return useMutation({
        ...options,
        onSuccess: async (data, variables, context) => {
            await queryClient.invalidateQueries({ queryKey: ["channels"] });
            await queryClient.invalidateQueries({ queryKey: ["channels", variables.id] });
            options.onSuccess?.(data, variables, context);
        },
        mutationFn: ({ id, data }) => channelsApi.updateChannel(axiosPriv, id, data),
        onError: onServerError,
    });
}
